import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {shareReplay, switchMap, takeUntil, map, filter} from 'rxjs/operators';
import {StudiesStore} from '../common/studies.store';
import {DisplaySettings} from '@core/interfaces/common/pages';
import {PagesStore} from '../config/pages.store';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {NbToastrService} from '@nebular/theme';
import {ProgramManagementSensitivityStore} from './program-management-sensitivity.store';
import {Filter} from '@core/interfaces/system/system-common';
import {
    FilteredOptionsList,
    OutcomeChartRequest,
    OutcomeChartResponse,
    OutcomeKpiResponse,
    PmHeaderFilterData,
    PmReportingService,
} from '@core/interfaces/engin/program-management/pm-reporting';
import {ProbabilityTypeEnum} from '@core/interfaces/engin/analyzer';

@Injectable()
export class OutcomesStore extends Unsubscribable {
    public resultsLoading = new BehaviorSubject<boolean>(false);
    public resultsLoading$ = this.resultsLoading.asObservable();

    public pageFilters = new BehaviorSubject<Filter[]>([]);
    public pageFilters$ = this.pageFilters.asObservable();
    public selectedOutcomesOptions: BehaviorSubject<PmHeaderFilterData> = new BehaviorSubject<PmHeaderFilterData>(null);
    public filteredOutcomesOptions: BehaviorSubject<FilteredOptionsList> = new BehaviorSubject<FilteredOptionsList>(
        null,
    );
    public selectedInvestmentsOptions: BehaviorSubject<PmHeaderFilterData> = new BehaviorSubject<PmHeaderFilterData>(
        null,
    );
    public filteredInvestmentsOptions: BehaviorSubject<FilteredOptionsList> = new BehaviorSubject<FilteredOptionsList>(
        null,
    );

    private probabilityType = new BehaviorSubject<ProbabilityTypeEnum>(ProbabilityTypeEnum.PDF);

    constructor(
        private pmReportingService: PmReportingService,
        private pmSensitivityStore: ProgramManagementSensitivityStore,
        private studiesStore: StudiesStore,
        private pagesStore: PagesStore,
        protected toastrService: NbToastrService,
    ) {
        super();
    }

    // TODO: get current status, filter for true; get data load, filter for false
    public combineChartRequestInfo$ = combineLatest<
        Observable<number>,
        Observable<number>,
        Observable<DisplaySettings>,
        Observable<Filter[]>
    >([
        this.studiesStore.activeStudyIdRisk$,
        this.pmSensitivityStore.sensitivityId$,
        this.pagesStore.currentDisplay$,
        this.pageFilters$,
    ]).pipe(
        shareReplay(1),
        filter(([studyId, sensitivityId]) => studyId !== null && sensitivityId != null),
        switchMap(([studyId, sensitivityId, displaySettings, filters]): Observable<OutcomeChartRequest> => {
            return of(this.prepareOutcomesChartRequest(studyId, displaySettings, sensitivityId, filters));
        }),
    );

    // Investment plan
    readonly chartDataSummarySpending$: Observable<OutcomeChartResponse> = this.combineChartRequestInfo$.pipe(
        switchMap((req: OutcomeChartRequest) => {
            return this.pmReportingService.getChartDataSummarySpending(req).pipe(map((res) => res.response));
        }),
        takeUntil(this.unsubscribe$),
        shareReplay(1),
    );
    readonly chartDataSummaryOutcomes$: Observable<OutcomeKpiResponse> = this.combineChartRequestInfo$.pipe(
        switchMap((req: OutcomeChartRequest) => {
            return this.pmReportingService.getChartDataSummaryOutcomes(req).pipe(map((res) => res.response));
        }),
        takeUntil(this.unsubscribe$),
        shareReplay(1),
    );

    // Investment forecast breakdown
    readonly chartDataProactiveForecast$: Observable<OutcomeChartResponse> = this.combineChartRequestInfo$.pipe(
        switchMap((req: OutcomeChartRequest) => {
            return this.pmReportingService.getChartDataProactiveForecast(req).pipe(map((res) => res.response));
        }),
        takeUntil(this.unsubscribe$),
        shareReplay(1),
    );
    readonly chartDataReactiveForecast$: Observable<OutcomeChartResponse> = this.combineChartRequestInfo$.pipe(
        switchMap((req: OutcomeChartRequest) => {
            return this.pmReportingService.getChartDataReactiveForecast(req).pipe(map((res) => res.response));
        }),
        takeUntil(this.unsubscribe$),
        shareReplay(1),
    );

    // Failure forecast breakdown
    readonly chartDataFailureForecast$: Observable<OutcomeChartResponse> = this.combineChartRequestInfo$.pipe(
        switchMap((req: OutcomeChartRequest) => {
            return this.pmReportingService.getChartDataFailureForecast(req).pipe(map((res) => res.response));
        }),
        takeUntil(this.unsubscribe$),
        shareReplay(1),
    );
    readonly chartDataOutageForecast$: Observable<OutcomeChartResponse> = this.combineChartRequestInfo$.pipe(
        switchMap((req: OutcomeChartRequest) => {
            return this.pmReportingService.getChartDataOutageForecast(req).pipe(map((res) => res.response));
        }),
        takeUntil(this.unsubscribe$),
        shareReplay(1),
    );

    // Risk forecast breakdown
    readonly chartDataRiskForecastByImpact$: Observable<OutcomeChartResponse> = this.combineChartRequestInfo$.pipe(
        switchMap((req: OutcomeChartRequest) => {
            return this.pmReportingService.getChartDataRiskForecastByImpact(req).pipe(map((res) => res.response));
        }),
        takeUntil(this.unsubscribe$),
        shareReplay(1),
    );
    readonly chartDataRiskForecastByCategory$: Observable<OutcomeChartResponse> = this.combineChartRequestInfo$.pipe(
        switchMap((req: OutcomeChartRequest) => {
            return this.pmReportingService.getChartDataRiskForecastByCategory(req).pipe(map((res) => res.response));
        }),
        takeUntil(this.unsubscribe$),
        shareReplay(1),
    );

    private prepareOutcomesChartRequest(
        studyId: number,
        displaySettings: DisplaySettings,
        sensetivityId: number,
        filters: Filter[],
    ): OutcomeChartRequest {
        return {
            filters: filters,
            graphList: displaySettings.graphList,
            sensitivityId: sensetivityId,
            workflowItemId: studyId,
            probabilityType: this.probabilityType.getValue(),
        } as OutcomeChartRequest;
    }

    public setFilters(filter: Filter[], probabilityType: ProbabilityTypeEnum) {
        this.probabilityType.next(probabilityType);
        this.pageFilters.next(filter);
    }
}
